import {
  APIEndpoint,
  IAPIStatusCode,
  IAPIStatusType,
  messages,
} from "../APIEndpointConfig";

import { GBSCError } from "@/models/GBSCError";
import {
  IAppointmentReportResponse,
  IExportReportsResponse,
  IMedicalPartnerResponse,
  IScheduleReportResponse,
  IStaffReportResponse,
  IStaffReportStatusResponse,
} from "@/models/ApiRequestResponse/report";

import { get, patch } from "@/utils/TypedApi";
import { FilterState } from "@/models/reports/innerScheduleReport";

import {
  ConsultationTypeMap,
  FilterKeys,
  KeyMap,
} from "@/constants/reports/Appointment";

export const getMedicalPartnerApi = async (): Promise<
  IMedicalPartnerResponse | GBSCError
> => {
  try {
    const response = await get<IMedicalPartnerResponse>(
      APIEndpoint.doctor.report.medicalPartner
    );
    if (response.type === IAPIStatusType.SUCCESS) {
      return response.data;
    }
    const { errorMessage, message, statusCode } = response;
    return new GBSCError(errorMessage, message, statusCode);
  } catch (ex) {
    console.log(ex);
  }
  return new GBSCError(
    IAPIStatusType.ERROR,
    messages.somethingWentWrong,
    IAPIStatusCode.INTERNAL_SERVER_ERROR
  );
};

export const getStaffReportApi = async (): Promise<
  IStaffReportResponse | GBSCError
> => {
  try {
    const response = await get<IStaffReportResponse>(
      APIEndpoint.doctor.report.staffReports
    );
    if (response.type === IAPIStatusType.SUCCESS) {
      return response.data;
    }
    const { errorMessage, message, statusCode } = response;
    return new GBSCError(errorMessage, message, statusCode);
  } catch (ex) {
    console.log(ex);
  }
  return new GBSCError(
    IAPIStatusType.ERROR,
    messages.somethingWentWrong,
    IAPIStatusCode.INTERNAL_SERVER_ERROR
  );
};

export const getScheduleReportApi = async (
  startDate: string,
  endDate: string
): Promise<IScheduleReportResponse | GBSCError> => {
  try {
    const endpoint = `${APIEndpoint.doctor.report.scheduleReport}?startDate=${startDate}&endDate=${endDate}`;
    const response = await get<IScheduleReportResponse>(endpoint);

    if (response.type === IAPIStatusType.SUCCESS) {
      return response.data;
    }
    const { errorMessage, message, statusCode } = response;
    return new GBSCError(errorMessage, message, statusCode);
  } catch (ex) {
    console.log(ex);
  }

  return new GBSCError(
    IAPIStatusType.ERROR,
    messages.somethingWentWrong,
    IAPIStatusCode.INTERNAL_SERVER_ERROR
  );
};

interface PatchStaffReportStatusPayload {
  id: string;
  is_active: boolean;
}
export const patchStaffReportStatus = async ({
  id,
  is_active,
}: PatchStaffReportStatusPayload): Promise<
  IStaffReportStatusResponse | GBSCError
> => {
  try {
    const response = await patch<IStaffReportStatusResponse>(
      `${APIEndpoint.doctor.report.staffReportStatus}/${id}`,
      { isActive: is_active },
      false
    );

    if (response.type === IAPIStatusType.SUCCESS) {
      return {
        ...response.data,
        id,
        is_active,
      };
    }

    const { errorMessage, message, statusCode } = response;
    return new GBSCError(errorMessage, message, statusCode);
  } catch (ex) {
    console.error("Error in patchStaffReportStatus:", ex);
    return new GBSCError(
      IAPIStatusType.ERROR,
      messages.somethingWentWrong,
      IAPIStatusCode.INTERNAL_SERVER_ERROR
    );
  }
};

export const getAppointmentReportApi = async (
  startDate: string,
  endDate: string,
  filters: Partial<FilterState>
): Promise<IAppointmentReportResponse | GBSCError> => {
  try {
    const params = new URLSearchParams({
      startDate,
      endDate,
    });

    Object.entries(filters).forEach(([key, value]) => {
      if (!value) {
        return;
      }
      let finalValue = value;
      switch (key) {
        case FilterKeys.CONSULTATION_TYPE:
          finalValue = ConsultationTypeMap[value] || value;
          break;
        case FilterKeys.CLINIC:
          key = KeyMap.CLINICIDS;
          finalValue = value.split("_")[0];
          break;

        case FilterKeys.PATIENT_TYPE:
          finalValue = value.toUpperCase();
          break;
      }

      params.append(key, finalValue);
    });
    const endpoint = `${
      APIEndpoint.doctor.report.appointmentReport
    }?${params.toString()}`;
    const response = await get<IAppointmentReportResponse>(endpoint);

    if (response.type === IAPIStatusType.SUCCESS) {
      return response.data;
    }
    const { errorMessage, message, statusCode } = response;
    return new GBSCError(errorMessage, message, statusCode);
  } catch (ex) {
    console.log(ex);
  }
  return new GBSCError(
    IAPIStatusType.ERROR,
    messages.somethingWentWrong,
    IAPIStatusCode.INTERNAL_SERVER_ERROR
  );
};

export const getInnerScheduleReportApi = async (
  scheduleId: string
): Promise<IAppointmentReportResponse | GBSCError> => {
  try {
    const url = `${APIEndpoint.doctor.report.appointmentReport}?scheduleId=${scheduleId}`;

    const response = await get<IAppointmentReportResponse>(url);

    if (response.type === IAPIStatusType.SUCCESS) {
      return response.data;
    }

    const { errorMessage, message, statusCode } = response;
    return new GBSCError(errorMessage, message, statusCode);
  } catch (ex) {
    console.log(ex);
  }

  return new GBSCError(
    IAPIStatusType.ERROR,
    messages.somethingWentWrong,
    IAPIStatusCode.INTERNAL_SERVER_ERROR
  );
};

export const getExportReports = async (
  reportEndpoint: string,
  dateRange?: { startDate: string; endDate: string }
): Promise<IExportReportsResponse | GBSCError> => {
  try {
    let finalEndpoint = reportEndpoint;
    if (dateRange?.startDate && dateRange?.endDate) {
      finalEndpoint += `&startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
    }
    const response = await get<IExportReportsResponse>(finalEndpoint);
    if (response.type === IAPIStatusType.SUCCESS) {
      return response.data;
    }
    const { errorMessage, message, statusCode } = response;
    return new GBSCError(errorMessage, message, statusCode);
  } catch (ex) {
    console.log(ex);
  }
  return new GBSCError(
    IAPIStatusType.ERROR,
    messages.somethingWentWrong,
    IAPIStatusCode.INTERNAL_SERVER_ERROR
  );
};
